import { InputVariant } from "@/contexts/PageContext/lib/theming/variants";
import { FormFieldAttributes } from "@/types/landing";
import { ChangeEventHandler, useState, useRef, useEffect } from "react";

export type InputProps = {
    variant: InputVariant;
    attributes: FormFieldAttributes;
    onChange: ChangeEventHandler<HTMLInputElement> | undefined;
    lastInputIndex: number;
    bqMapping: string;
    styles: { readonly [key: string]: string };
    isInPopupAndNotBannerflow: boolean;
    index: number;
};

const isAtLeast18 = (birthDate: Date): boolean => {
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        return age - 1 >= 18;
    }
    
    return age >= 18;
};

export const Input = ({
    attributes,
    lastInputIndex,
    onChange,
    bqMapping,
    styles,
    isInPopupAndNotBannerflow,
    index,
    variant,
}: InputProps) => {
    const [hasBeenInteracted, setHasBeenInteracted] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    
    // Refocus the input after type changes from text to date
    useEffect(() => {
        if (hasBeenInteracted && attributes.fieldType === "dob") {
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.click();
            }, 0);
        }
    }, [hasBeenInteracted, attributes.fieldType]);
    
    const handleDateChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const input = e.target;
        const birthDate = new Date(input.value);
    
        if (attributes.fieldType === "dob") {
            if (!isAtLeast18(birthDate)) {
                input.setCustomValidity("You must be 18+ years old to continue");
            } else {
                input.setCustomValidity("");
            }
        }
        
        onChange?.(e);
    };

    const handleInteraction = () => {
        if (attributes.fieldType === "dob" && !hasBeenInteracted) {
            setHasBeenInteracted(true);
        }
    };

    const placeholderText = attributes?.placeholder ?? (attributes?.fieldType === "dob" ? "MM-DD-YYYY" : undefined);

    const getInputType = () => {
        if (attributes.fieldType === "dob") {
            if (!hasBeenInteracted) {
                return "text";
            }
            return "date";
        }
        return attributes.fieldType;
    };

    return (
        <div className={styles.inputWrapper}>
            <input
                ref={inputRef}
                className={[
                    styles.input,
                    index === lastInputIndex ? styles.lastInput : "",
                    isInPopupAndNotBannerflow
                        ? styles.inPopupNotBannerflowInput
                        : "",
                    attributes.fieldType === "dob" ? styles.dateInput : "",
                ].join(" ")}
                name={bqMapping}
                autoComplete={attributes?.autoComplete}
                type={getInputType()}
                placeholder={placeholderText}
                required={attributes?.isRequired}
                pattern={
                    attributes.fieldType === "tel"
                        ? "^(?:\\+\\d{1,3})?\\s?\\d{3,4}\\s?\\d{3,4}\\s?\\d{3}$"
                        : undefined
                }
                onChange={handleDateChange}
                onMouseDown={handleInteraction}
                onTouchStart={handleInteraction}
                onClick={handleInteraction}
                onFocus={handleInteraction}
                max={attributes.fieldType === "dob" ? new Date().toISOString().split('T')[0] : undefined}
            />
        </div>
    );
};
